import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  ArrowUpward,
  ArrowDownward,
  Today,
  Event,
  Psychology,
} from '@mui/icons-material';
import useMlPredictions from '../hooks/useMlPredictions';
import useLiveGoldPrice from '../hooks/useLiveGoldPrice';

const formatPrice = (n) => {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function buildLiveForecast(ml, livePrice) {
  const base = livePrice ?? ml?.live?.price ?? ml?.current?.close;
  if (base == null || !ml?.predictions) return null;

  const r = ml.returns || {};
  const dayEndClose = r.dayEndClose != null ? base * (1 + r.dayEndClose) : ml.predictions.dayEndClose;
  const nextDayOpen = r.nextDayOpen != null ? base * (1 + r.nextDayOpen) : ml.predictions.nextDayOpen;
  let nextDayClose = r.nextDayClose != null ? base * (1 + r.nextDayClose) : ml.predictions.nextDayClose;
  let nextDayHigh = r.nextDayHigh != null ? base * (1 + r.nextDayHigh) : ml.predictions.nextDayHigh;
  let nextDayLow = r.nextDayLow != null ? base * (1 + r.nextDayLow) : ml.predictions.nextDayLow;

  nextDayHigh = Math.max(nextDayHigh, nextDayOpen, nextDayClose, base);
  nextDayLow = Math.min(nextDayLow, nextDayOpen, nextDayClose, base);

  const todayMove = dayEndClose - base;
  const todayMovePct = (todayMove / base) * 100;
  const todayDir = todayMove >= 0 ? 'UP' : 'DOWN';

  const tomMove = nextDayClose - base;
  const tomMovePct = (tomMove / base) * 100;
  const tomDir = tomMove >= 0 ? 'UP' : 'DOWN';

  return {
    base,
    dayEndClose,
    nextDayOpen,
    nextDayClose,
    nextDayHigh,
    nextDayLow,
    todayMove,
    todayMovePct,
    todayDir,
    tomMove,
    tomMovePct,
    tomDir,
    confidence: ml.predictions.confidence ?? 70,
  };
}

const DirChip = ({ dir, pct }) => {
  const up = dir === 'UP';
  return (
    <Chip
      icon={up ? <ArrowUpward /> : <ArrowDownward />}
      label={`${dir} ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`}
      color={up ? 'success' : 'error'}
      sx={{ fontWeight: 'bold' }}
    />
  );
};

const PriceBox = ({ label, value, color, hint }) => (
  <Paper
    sx={{
      p: 2,
      textAlign: 'center',
      bgcolor: '#1A1A1A',
      border: '1px solid',
      borderColor: color === 'success.main' ? '#00C853' : color === 'error.main' ? '#ff4444' : '#333',
      height: '100%',
    }}
  >
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {label}
    </Typography>
    <Typography variant="h4" fontWeight="bold" color={color || 'inherit'}>
      {formatPrice(value)}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
        {hint}
      </Typography>
    )}
  </Paper>
);

const MLPredictionPanel = ({ mode = 'both' }) => {
  const { data, loading, error } = useMlPredictions();
  const live = useLiveGoldPrice(30000);

  const forecast = useMemo(
    () => (data ? buildLiveForecast(data, live.price) : null),
    [data, live.price]
  );

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={2}>
        <CircularProgress />
        <Typography color="text.secondary">Loading predictions...</Typography>
      </Box>
    );
  }

  if (error || !data || !forecast) {
    return (
      <Alert severity="warning">
        {error || 'No predictions. Run: npm run train && npm run predict'}
      </Alert>
    );
  }

  const p = forecast;
  const showToday = mode === 'both' || mode === 'today';
  const showTomorrow = mode === 'both' || mode === 'tomorrow';

  return (
    <Box>
      {(mode === 'today' || mode === 'both') && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<Psychology />}>
          Live spot {formatPrice(p.base)} · Confidence {p.confidence}%
        </Alert>
      )}

      {/* ========== TODAY ========== */}
      {showToday && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: showTomorrow ? 3 : 0,
            background: `linear-gradient(135deg, ${p.todayDir === 'UP' ? '#1a3a1a' : '#3a1a1a'} 0%, #1A1A1A 100%)`,
            border: `2px solid ${p.todayDir === 'UP' ? '#00C853' : '#ff4444'}`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              <Today sx={{ mr: 1, verticalAlign: 'middle' }} />
              TODAY — Day End Prediction
            </Typography>
            <DirChip dir={p.todayDir} pct={p.todayMovePct} />
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            From now until session close — where price is likely to settle
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <PriceBox label="Live Spot (Now)" value={p.base} color="primary.main" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <PriceBox
                label="Day-End Close"
                value={p.dayEndClose}
                color={p.todayDir === 'UP' ? 'success.main' : 'error.main'}
                hint={`${p.todayMove >= 0 ? '+' : ''}${p.todayMove.toFixed(2)} from live`}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#1A1A1A', border: '1px solid #333', height: '100%' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Confidence
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {p.confidence}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={p.confidence}
                  color="success"
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                />
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="body1" mt={2} fontWeight="bold" color={p.todayDir === 'UP' ? 'success.main' : 'error.main'}>
            {p.todayDir === 'UP'
              ? `Today's close may settle near ${formatPrice(p.dayEndClose)} (slightly higher)`
              : `Today's close may settle near ${formatPrice(p.dayEndClose)} (slightly lower)`}
          </Typography>
        </Paper>
      )}

      {/* ========== TOMORROW ========== */}
      {showTomorrow && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            background: `linear-gradient(135deg, ${p.tomDir === 'UP' ? '#1a3a1a' : '#3a1a1a'} 0%, #1A1A1A 100%)`,
            border: `2px solid ${p.tomDir === 'UP' ? '#00C853' : '#ff4444'}`,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              <Event sx={{ mr: 1, verticalAlign: 'middle' }} />
              TOMORROW — Next Day Prediction
            </Typography>
            <DirChip dir={p.tomDir} pct={p.tomMovePct} />
          </Box>

          <Typography variant="body2" color="text.secondary" mb={2}>
            Tomorrow's open, close, and high–low range
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <PriceBox label="Tomorrow Open" value={p.nextDayOpen} />
            </Grid>
            <Grid item xs={6} md={3}>
              <PriceBox
                label="Tomorrow Close"
                value={p.nextDayClose}
                color={p.tomDir === 'UP' ? 'success.main' : 'error.main'}
                hint={`${p.tomMove >= 0 ? '+' : ''}${p.tomMove.toFixed(2)} vs live`}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <PriceBox label="High (up to)" value={p.nextDayHigh} color="success.main" />
            </Grid>
            <Grid item xs={6} md={3}>
              <PriceBox label="Low (down to)" value={p.nextDayLow} color="error.main" />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2, borderColor: '#333' }} />

          <Typography variant="body1" fontWeight="bold" color={p.tomDir === 'UP' ? 'success.main' : 'error.main'}>
            {`Tomorrow close ~${formatPrice(p.nextDayClose)} — expected range ${formatPrice(p.nextDayLow)} to ${formatPrice(p.nextDayHigh)}`}
          </Typography>
        </Paper>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={2} textAlign="center">
        Live spot × ML returns · Educational only · Not financial advice
      </Typography>
    </Box>
  );
};

export default MLPredictionPanel;
